import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi, errorMessage } from "@/api";
import MotoStep from "./steps/MotoStep";
import UbicacionStep from "./steps/UbicacionStep";
import CoberturasStep from "./steps/CoberturasStep";
import DatosStep from "./steps/DatosStep";
import ResumenStep from "./steps/ResumenStep";
import SinCotizacionStep from "./steps/SinCotizacionStep";
import PagoStep from "./steps/PagoStep";
import CotizandoOverlay from "./CotizandoOverlay";
import { Footer } from "./Footer";

const STEP_TITLES = {
  moto: "Tu moto",
  ubicacion: "Tu zona",
  coberturas: "Elegi tu cobertura",
  datos: "Tus datos",
  resumen: "Resumen y pago",
  "sin-cotizacion": "Sin cotización",
  pago: "Pago y emision",
};

const STEP_PATHS = {
  moto: "/", ubicacion: "/ubicacion", coberturas: "/coberturas",
  datos: "/datos", resumen: "/resumen", "sin-cotizacion": "/sin-cotizacion",
  pago: "/pago",
};
const PATH_STEPS = Object.fromEntries(Object.entries(STEP_PATHS).map(([s, p]) => [p.replace(/^\//, ""), s]));
const SESSION_KEY = "spm_wizard_session";
const SESSION_ALIVE = "spm_wizard_alive";

function injectPixels(html) {
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    if (old.src) s.src = old.src;
    else s.textContent = old.textContent;
    document.head.appendChild(s);
  });
}

function esValido(urlStep, s) {
  return (
    urlStep === "moto" ||
    (urlStep === "ubicacion" && s.moto?.marca) ||
    (urlStep === "coberturas" && s.cot) ||
    (urlStep === "datos" && s.cot && s.cobertura) ||
    (urlStep === "resumen" && s.cot && s.cobertura && s.titular) ||
    (urlStep === "sin-cotizacion" && s.cot) ||
    (urlStep === "pago" && s.emitResult)
  );
}

export default function Wizard() {
  const [boot, setBoot] = useState(null);
  const [bootError, setBootError] = useState("");
  const [step, setStep] = useState("moto");
  const [moto, setMoto] = useState({ marca: "", marca_desc: "", modelo: "", modelo_desc: "", tau_codia: "", anio: "", tipo_uso: 1, sin_rodar: false, comercial_consent: false });
  const [ubicacion, setUbicacion] = useState(null);
  const [cot, setCot] = useState(null);
  const [cobertura, setCobertura] = useState(null);
  const [emitResult, setEmitResult] = useState(null);
  const [pagoHabilitado, setPagoHabilitado] = useState(false);
  const [titular, setTitular] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const [restored, setRestored] = useState(false);
  const [recError, setRecError] = useState("");
  const recoverToken = params.token || (typeof window !== "undefined" && (window.location.pathname.startsWith("/recuperar/") || window.location.pathname.startsWith("/mp/")) ? decodeURIComponent(window.location.pathname.split("/").filter(Boolean)[1] || "") : "");
  const llavePago = params.llave || "";

  // Reglas de reinicio:
  // - App recien abierta (PWA/pestana nueva): se borra todo y se empieza de cero.
  // - Recarga en el inicio ("/"): se borra todo y se empieza de cero.
  // - Recarga en un paso intermedio (misma pestana): restaura la sesion.
  useEffect(() => {
    if (recoverToken) return; // ruta de recuperacion: se maneja aparte
    const freshStart = !sessionStorage.getItem(SESSION_ALIVE);
    sessionStorage.setItem(SESSION_ALIVE, "1");
    const urlStep = PATH_STEPS[params.paso || ""] || "moto";
    if (freshStart || urlStep === "moto") {
      localStorage.removeItem(SESSION_KEY);
      setRestored(true);
      if (urlStep !== "moto") navigate("/", { replace: true });
      return;
    }
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.moto) setMoto(s.moto);
        if (s.ubicacion) setUbicacion(s.ubicacion);
        if (s.cot) setCot(s.cot);
        if (s.cobertura) setCobertura(s.cobertura);
        if (s.titular) setTitular(s.titular);
        if (s.emitResult) setEmitResult(s.emitResult);
        setStep(esValido(urlStep, s) ? urlStep : "moto");
      }
    } catch { /* sesion corrupta: arrancar de cero */ }
    setRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recuperacion de una cotizacion guardada/emitida via link (/recuperar/:token).
  // Rehidrata el estado en el mismo componente (sin recargar ni navegar).
  useEffect(() => {
    if (!recoverToken) return;
    sessionStorage.setItem(SESSION_ALIVE, "1");
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/recuperar/${recoverToken}`, { params: llavePago ? { llave: llavePago } : {} });
        setMoto({ marca: String(data.moto?.marca || ""), marca_desc: data.moto?.marca_desc || "", modelo: String(data.moto?.modelo || ""), modelo_desc: data.moto?.modelo_desc || "", tau_codia: "", anio: String(data.moto?.anio || ""), tipo_uso: data.moto?.tipo_uso || 1, sin_rodar: !!data.moto?.sin_rodar, cerokm_consent: !!data.moto?.sin_rodar, comercial_consent: false });
        setCot({ operacion_id: data.operacion_id, operacion: data.operacion, coberturas: data.coberturas || [], datos_cotiz: data.datos_cotiz || {}, cupon: data.cupon || null });
        setCobertura(data.cobertura || null);
        setPagoHabilitado(Boolean(data.pago_habilitado));
        setTitular(data.titular || null);
        if (data.ya_emitida) {
          setEmitResult({ operacion_id: data.operacion_id, estado: data.estado, linkpago: data.linkpago, npoliza: data.npoliza, solicitud: data.solicitud, whatsapp_url: "" });
          setStep("pago");
        } else {
          setStep("resumen");
        }
      } catch (err) {
        setRecError(errorMessage(err, "No pudimos recuperar tu cotización. El link puede haber expirado."));
      } finally {
        setRestored(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoverToken]);

  // Sincronizar URL con el paso + tracking de page_view (GA4 / Meta Pixel)
  useEffect(() => {
    if (!restored) return;
    if (recoverToken) return;
    const path = STEP_PATHS[step] || "/";
    if (window.location.pathname !== path) navigate(path);
    const meta = { page_path: path, page_title: STEP_TITLES[step] || step };
    if (window.gtag) window.gtag("event", "page_view", meta);
    if (window.fbq) window.fbq("track", "PageView");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, restored]);

  // Persistir la sesion del wizard por si recarga
  useEffect(() => {
    if (!restored) return;
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ step, moto, ubicacion, cot, cobertura, titular, emitResult }));
    } catch { /* sin espacio */ }
  }, [step, moto, ubicacion, cot, cobertura, titular, emitResult, restored]);

  // Boton atras/adelante del navegador
  useEffect(() => {
    if (!restored) return;
    if (recoverToken) return;
    const urlStep = PATH_STEPS[params.paso || ""] || "moto";
    if (urlStep === step) return;
    if (esValido(urlStep, { moto, ubicacion, cot, cobertura, titular, emitResult })) setStep(urlStep);
    else navigate(STEP_PATHS[step] || "/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.paso]);

  useEffect(() => {
    publicApi.get("/public/params/bootstrap")
      .then(({ data }) => {
        setBoot(data);
        injectPixels(data.pixeles);
      })
      .catch(() => setBootError("No pudimos cargar el cotizador. Revisa tu conexion e intenta de nuevo."));
  }, []);

  const flow = ["moto", "ubicacion", "coberturas", "datos", "resumen", "pago"];
  const stepIndex = flow.indexOf(step);

  const goBack = () => {
    if (stepIndex > 0) setStep(flow[stepIndex - 1]);
  };

  const compartir = async () => {
    publicApi.post("/public/share").catch(() => {});
    const data = {
      title: "Seguros Para Moto",
      text: "Cotiza y contrata el seguro de tu moto en 3 minutos. Las polizas son emitidas por ATM Seguros.",
      url: window.location.origin,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(data.url);
        toast.success("Enlace copiado, listo para compartir");
      }
    } catch { /* el usuario cancelo */ }
  };

  const handleCotizar = useCallback(async (ubic) => {
    setLoading(true);
    setUbicacion(ubic);
    try {
      const { data } = await publicApi.post("/public/cotizar", {
        marca: Number(moto.marca),
        modelo: Number(moto.modelo),
        anio: Number(moto.anio),
        tipo_uso: Number(moto.tipo_uso),
        cp: ubic.cp,
        sub_cp: ubic.sub_cp || null,
        cerokm: Boolean(moto.sin_rodar),
        marca_desc: moto.marca_desc,
        modelo_desc: moto.modelo_desc,
        localidad_desc: ubic.localidad_desc,
      });
      if (data.sin_cotizacion || !data.coberturas.length) {
        setCot(data);
        setStep("sin-cotizacion");
        return;
      }
      setCot(data);
      setStep("coberturas");
    } catch (err) {
      toast.error(errorMessage(err, "No pudimos cotizar. Verifica los datos e intenta de nuevo."));
    } finally {
      setLoading(false);
    }
  }, [moto]);

  const handleEmitir = useCallback(async (extra = {}) => {
    setLoading(true);
    try {
      const payload = {
        operacion_id: cot.operacion_id,
        cobertura: cobertura.codigo,
        plan_cot: cobertura.plan_cot || "",
        titular,
        vehiculo: { patente: titular.patente, chasis: titular.chasis, motor: titular.motor, sin_rodar: Boolean(titular.sin_rodar) },
        acepta_terminos: true,
        ...extra,
      };
      const { data } = await publicApi.post("/public/emitir", payload);
      setEmitResult(data);
      setStep("pago");
    } catch (err) {
      toast.error(errorMessage(err, "No pudimos emitir la poliza. Revisa los datos."));
    } finally {
      setLoading(false);
    }
  }, [cot, cobertura, titular]);

  const handleGuardar = useCallback(async (extra = {}) => {
    const payload = {
      operacion_id: cot.operacion_id,
      cobertura: cobertura.codigo,
      plan_cot: cobertura.plan_cot || "",
      titular,
      vehiculo: { patente: titular.patente, chasis: titular.chasis, motor: titular.motor, sin_rodar: Boolean(titular.sin_rodar) },
      acepta_terminos: true,
      origin: window.location.origin,
      ...extra,
    };
    const { data } = await publicApi.post("/public/guardar", payload);
    return data;
  }, [cot, cobertura, titular]);

  const handleCancelar = useCallback(async () => {
    const { data } = await publicApi.post("/public/cancelar", {
      operacion_id: cot.operacion_id, cobertura: cobertura.codigo, plan_cot: cobertura.plan_cot || "",
      titular, vehiculo: { patente: titular.patente, chasis: titular.chasis, motor: titular.motor, sin_rodar: Boolean(titular.sin_rodar) },
    });
    return data;
  }, [cot, cobertura, titular]);

  const handleConsultar = useCallback(async () => {
    const { data } = await publicApi.post("/public/consulta", {
      operacion_id: cot.operacion_id, cobertura: cobertura.codigo, plan_cot: cobertura.plan_cot || "",
      titular, vehiculo: { patente: titular.patente, chasis: titular.chasis, motor: titular.motor, sin_rodar: Boolean(titular.sin_rodar) },
    });
    return data;
  }, [cot, cobertura, titular]);

  const handleAplicarCupon = useCallback(async (codigo) => {
    const { data } = await publicApi.post("/public/cupon/aplicar", { operacion_id: cot.operacion_id, codigo });
    setCot((prev) => ({ ...prev, coberturas: data.coberturas, datos_cotiz: data.datos_cotiz || prev.datos_cotiz, cupon: data.cupon }));
    return data;
  }, [cot]);

  const handleQuitarCupon = useCallback(async () => {
    const { data } = await publicApi.post("/public/cupon/quitar", { operacion_id: cot.operacion_id });
    setCot((prev) => ({ ...prev, coberturas: data.coberturas, cupon: null }));
    return data;
  }, [cot]);

  if (recoverToken && (!restored || recError)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="wizard-recuperando">
        {recError
          ? <div className="text-slate-600">{recError}<br /><a href="/" className="text-emerald-700 underline underline-offset-2 mt-2 inline-block">Volver al inicio</a></div>
          : <div className="animate-pulse text-slate-500">Recuperando tu cotización...</div>}
      </div>
    );
  }
  if (bootError) {
    return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="wizard-error">{bootError}</div>;
  }
  if (!boot) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="wizard-loading"><div className="animate-pulse text-slate-500">Cargando cotizador...</div></div>;
  }
  if (boot.cotizador?.apagado && !recoverToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6 text-center bg-[#F8FAFC]" data-testid="cotizador-apagado">
        <img src="/assets/logo-spm.png" alt={boot.branding.empresa} className="h-10 w-auto" />
        <p className="max-w-md text-lg text-slate-700 whitespace-pre-line" data-testid="cotizador-apagado-mensaje">{boot.cotizador.mensaje}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="wizard">
      <header className="bg-white/95 backdrop-blur border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
          <button type="button" data-testid="brand-logo-home" onClick={() => { window.location.href = "/"; }} className="shrink-0 cursor-pointer" aria-label="Ir al inicio">
            <img src="/assets/logo-spm.png" alt={boot.branding.empresa} className="h-7 sm:h-10 w-auto" data-testid="brand-logo" />
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-2 sm:px-2.5 py-1.5">
              <img src={boot.branding.logo_atm_url} alt="ATM Seguros" className="h-5 sm:h-4 w-auto" />
              <span className="hidden min-[430px]:inline text-[10px] sm:text-[11px] text-slate-600 font-medium whitespace-nowrap">Emite ATM Seguros</span>
            </div>
            <button type="button" data-testid="share-button" onClick={compartir}
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-white text-[11px] sm:text-xs font-heading font-semibold bg-[#1B3A5C] hover:bg-[#142c46] transition-colors whitespace-nowrap">
              <Share2 className="w-4 h-4" /> Compartir
            </button>
          </div>
        </div>
        {stepIndex >= 0 && (
          <div className="max-w-xl mx-auto px-4 pb-3">
            <div className="text-xs text-slate-500 mb-1.5">
              <span data-testid="wizard-step-title" className="font-semibold text-slate-700">{STEP_TITLES[step]}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full transition-all duration-500" style={{ width: `${((stepIndex + 1) / flow.length) * 100}%` }} />
            </div>
          </div>
        )}
      </header>

      {loading && step === "ubicacion" && <CotizandoOverlay />}
      <main className="flex-1 max-w-xl mx-auto w-full px-4 py-6 pb-32">
        <div key={step}>
            {step === "moto" && <MotoStep boot={boot} value={moto} onChange={setMoto} onNext={() => setStep("ubicacion")} onBack={goBack} />}
            {step === "ubicacion" && <UbicacionStep onNext={handleCotizar} onBack={goBack} loading={loading} />}
            {step === "coberturas" && cot && (
              <CoberturasStep cot={cot} config={boot.coberturas_config} moto={moto}
                onBack={goBack} onAplicarCupon={handleAplicarCupon} onQuitarCupon={handleQuitarCupon}
                onNext={(cob) => { setCobertura(cob); setStep("datos"); }} />
            )}
            {step === "datos" && (
              <DatosStep boot={boot} ubicacion={ubicacion} moto={moto} loading={loading} onBack={goBack} value={titular}
                onSubmit={(t) => { setTitular(t); setStep("resumen"); }} />
            )}
            {step === "resumen" && titular && cobertura && (
              <ResumenStep boot={boot} moto={moto} cobertura={cobertura} titular={titular} loading={loading}
                onBack={goBack} onEmitir={handleEmitir} onGuardar={handleGuardar} onCancelar={handleCancelar} onConsultar={handleConsultar} pagoHabilitado={pagoHabilitado} recuperado={Boolean(recoverToken)}
                onCambiarCobertura={() => setStep("coberturas")} onEditarDatos={() => setStep("datos")} />
            )}
            {step === "sin-cotizacion" && cot && <SinCotizacionStep cot={cot} moto={moto} onBack={() => setStep("moto")} />}
            {step === "pago" && emitResult && <PagoStep emit={emitResult} cobertura={cobertura} importante={boot.consentimiento?.importante_texto} moto={moto} titular={titular} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
